import { useMemo } from 'react';
import { Brain, ListChecks } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

export function DashboardPractice({
  subjects,
  topics,
  practiceSubject,
  setPracticeSubject,
  practiceTopic,
  setPracticeTopic,
  practiceDifficulty,
  setPracticeDifficulty,
  practiceCount,
  setPracticeCount,
  questions,
  questionsLoading,
  selectedAnswers,
  handleAnswerSelect,
  showResults,
  checkAnswers,
  getScore,
  generateQuestions,
}) {
  const availableTopics = useMemo(
    () => topics[practiceSubject] || [],
    [topics, practiceSubject]
  );

  return (
    <>
      <div className="dashboard-widget">
        <h3 className="font-bold text-[#0A0A0A] mb-6">Generate Practice Questions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Subject</label>
            <Select value={practiceSubject} onValueChange={setPracticeSubject}>
              <SelectTrigger className="rounded-sm" data-testid="practice-subject">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Topic</label>
            <Select value={practiceTopic} onValueChange={setPracticeTopic}>
              <SelectTrigger className="rounded-sm" data-testid="practice-topic">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTopics.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Difficulty</label>
            <Select value={practiceDifficulty} onValueChange={setPracticeDifficulty}>
              <SelectTrigger className="rounded-sm" data-testid="practice-difficulty">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Questions</label>
            <Select value={String(practiceCount)} onValueChange={(v) => setPracticeCount(Number(v))}>
              <SelectTrigger className="rounded-sm" data-testid="practice-count">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 Questions</SelectItem>
                <SelectItem value="5">5 Questions</SelectItem>
                <SelectItem value="10">10 Questions</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={generateQuestions}
          disabled={questionsLoading}
          className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm"
          data-testid="generate-questions"
        >
          {questionsLoading ? (
            <><div className="spinner w-4 h-4 mr-2"></div>Generating...</>
          ) : (
            <><Brain className="w-4 h-4 mr-2" />Generate Questions</>
          )}
        </Button>
      </div>

      {questions.length > 0 && (
        <div className="dashboard-widget">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-[#0A0A0A]">{practiceSubject} - {practiceTopic}</h3>
            {showResults && <span className="text-lg font-bold text-[#002FA7]">Score: {getScore()}/{questions.length}</span>}
          </div>
          <div className="space-y-6">
            {questions.map((q, qIndex) => (
              <div key={`pq-${qIndex}`} className="p-4 border border-[#E5E7EB] rounded-sm" data-testid={`question-${qIndex}`}>
                <p className="font-medium text-[#0A0A0A] mb-4">{qIndex + 1}. {q.question}</p>
                <div className="space-y-2">
                  {q.options?.map((option) => {
                    const letter = option.charAt(0);
                    const isSelected = selectedAnswers[qIndex] === letter;
                    const isCorrect = showResults && letter === q.correct_answer;
                    const isWrong = showResults && isSelected && letter !== q.correct_answer;
                    const borderClass = isCorrect
                      ? 'bg-green-50 border-green-500'
                      : isWrong
                        ? 'bg-red-50 border-red-500'
                        : isSelected
                          ? 'bg-[#002FA7]/10 border-[#002FA7]'
                          : 'border-[#E5E7EB] hover:border-[#002FA7]';

                    return (
                      <button
                        key={`${qIndex}-${letter}`}
                        onClick={() => handleAnswerSelect(qIndex, letter)}
                        className={`w-full text-left p-3 rounded-sm border transition-colors ${borderClass}`}
                        disabled={showResults}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
                {showResults && q.explanation && (
                  <div className="mt-4 p-3 bg-[#F9FAFB] rounded-sm">
                    <p className="text-sm text-[#4B5563]"><span className="font-medium">Explanation:</span> {q.explanation}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          {!showResults && Object.keys(selectedAnswers).length === questions.length && (
            <Button onClick={checkAnswers} className="mt-6 bg-[#002FA7] hover:bg-[#001B63] rounded-sm" data-testid="check-answers">
              <ListChecks className="w-4 h-4 mr-2" />Check Answers
            </Button>
          )}
          {showResults && (
            <Button onClick={generateQuestions} className="mt-6 bg-[#002FA7] hover:bg-[#001B63] rounded-sm">
              Generate New Questions
            </Button>
          )}
        </div>
      )}
    </>
  );
}
